/**
 * Fatiado de pages/ImportMenu.tsx por responsabilidade (#131, R3).
 */
import { toast } from '../../components/ui/index.js'

// ─── Normalização client-side ─────────────────────────────────────────────────

const MAX_DIM = 1600

export async function imageToJpeg(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()
  return await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.85))
}

async function pdfToJpegs(file: File): Promise<Blob[]> {
  const pdfjs = await import('pdfjs-dist')
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl
  const data = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data }).promise
  const pages: Blob[] = []
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n)
    const base = page.getViewport({ scale: 1 })
    const scale = Math.min(2, MAX_DIM / Math.max(base.width, base.height))
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(viewport.width)
    canvas.height = Math.round(viewport.height)
    await page.render({
      canvas,
      canvasContext: canvas.getContext('2d')!,
      viewport,
    }).promise
    pages.push(
      await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.85)),
    )
  }
  return pages.slice(0, 20)
}

export type PreparedPage = { blob: Blob; preview: string }

export async function prepareFiles(files: File[]): Promise<PreparedPage[]> {
  const out: PreparedPage[] = []
  for (const file of files) {
    try {
      if (file.type === 'application/pdf') {
        for (const blob of await pdfToJpegs(file)) {
          out.push({ blob, preview: URL.createObjectURL(blob) })
        }
      } else if (file.type.startsWith('image/')) {
        const blob = await imageToJpeg(file)
        out.push({ blob, preview: URL.createObjectURL(blob) })
      }
    } catch {
      toast.error(file.name)
    }
  }
  return out.slice(0, 20)
}
